import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion } from 'framer-motion';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);

    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isDesktop;
}

function normalizeSrc(src) {
  return encodeURI(src);
}

function ImageTestimonialsGallery({
  title = 'Valoraciones de las familias',
  subtitle,
  desktopImages = [],
  mobileImages = [],
  sectionClassName = 'section-padding bg-background',
  titleClassName = 'text-primary',
  imageAlt = 'Testimonio de una familia',
}) {
  const isDesktop = useIsDesktop();
  const images = useMemo(() => {
    const selected = isDesktop ? desktopImages : mobileImages;
    return selected.length ? selected : desktopImages.length ? desktopImages : mobileImages;
  }, [desktopImages, isDesktop, mobileImages]);
  const [index, setIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const currentIndex = Math.min(index, Math.max(images.length - 1, 0));

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(images.length - 1, 0)));
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
      if (event.key === 'ArrowLeft') setIndex((current) => (current - 1 + images.length) % images.length);
      if (event.key === 'ArrowRight') setIndex((current) => (current + 1) % images.length);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [images.length, isOpen]);

  const showPrevious = () => setIndex((current) => (current - 1 + images.length) % images.length);
  const showNext = () => setIndex((current) => (current + 1) % images.length);

  if (!images.length) return null;

  return (
    <section className={sectionClassName}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center md:mb-12">
          <h2 className={`text-4xl md:text-6xl ${titleClassName}`}>{title}</h2>
          {subtitle && (
            <p className="mx-auto mt-4 max-w-4xl font-sans text-sm font-bold uppercase tracking-[0.14em] text-primary/90 md:text-base">
              {subtitle}
            </p>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="relative mx-auto max-w-6xl"
        >
            <button
              type="button"
              onClick={showPrevious}
              aria-label="Ver testimonio anterior"
              className="absolute -left-3 top-1/2 z-10 flex min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-primary shadow-md transition hover:scale-110 md:-left-8"
            >
              <ChevronLeft className="h-11 w-11" strokeWidth={2.4} />
            </button>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="-mx-4 block w-[calc(100%+2rem)] overflow-hidden rounded-[1.75rem] bg-white shadow-xl md:mx-0 md:w-full"
            aria-label="Abrir testimonio en pantalla completa"
          >
            <picture>
              <source media="(min-width: 768px)" srcSet={normalizeSrc(desktopImages[currentIndex] || images[currentIndex])} />
              <img
                src={normalizeSrc(images[currentIndex])}
                alt={`${imageAlt} ${currentIndex + 1}`}
                loading="lazy"
                decoding="async"
                className="w-full object-contain md:max-h-[78vh]"
              />
            </picture>
          </button>

            <button
              type="button"
              onClick={showNext}
              aria-label="Ver siguiente testimonio"
              className="absolute -right-3 top-1/2 z-10 flex min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-primary shadow-md transition hover:scale-110 md:-right-8"
            >
              <ChevronRight className="h-11 w-11" strokeWidth={2.4} />
            </button>
        </motion.div>

        <div className="mt-7 flex flex-wrap justify-center gap-2">
          {images.map((image, imageIndex) => (
            <button
              key={image}
              type="button"
              onClick={() => setIndex(imageIndex)}
              aria-label={`Ver testimonio ${imageIndex + 1}`}
              aria-current={imageIndex === currentIndex ? 'true' : undefined}
              className={`h-4 rounded-full transition-all duration-300 ${imageIndex === currentIndex ? 'w-10 bg-primary' : 'w-4 bg-primary/35'}`}
            />
          ))}
        </div>
      </div>

      {isOpen && (
        <div role="dialog" aria-modal="true" aria-label="Galería de testimonios" className="fixed inset-0 z-[100] flex items-center justify-center bg-[#A04428]/85 px-1 py-6 backdrop-blur-sm sm:px-4">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Cerrar testimonio"
            className="absolute right-4 top-4 z-20 flex min-h-12 min-w-12 items-center justify-center rounded-full text-white transition hover:scale-110 md:right-7 md:top-7"
          >
            <X className="h-10 w-10" strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={showPrevious}
            aria-label="Testimonio anterior"
            className="absolute left-0 top-1/2 z-20 flex min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full text-white transition hover:scale-110 md:left-7"
          >
            <ChevronLeft className="h-12 w-12" strokeWidth={2.4} />
          </button>
          <img
            src={normalizeSrc(images[currentIndex])}
            alt={`${imageAlt} ${currentIndex + 1}`}
            decoding="async"
            className="max-h-[calc(100dvh-4rem)] max-w-[96vw] rounded-2xl object-contain shadow-2xl md:max-h-[88vh] md:max-w-[92vw]"
          />
          <button
            type="button"
            onClick={showNext}
            aria-label="Siguiente testimonio"
            className="absolute right-0 top-1/2 z-20 flex min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full text-white transition hover:scale-110 md:right-7"
          >
            <ChevronRight className="h-12 w-12" strokeWidth={2.4} />
          </button>
        </div>
      )}
    </section>
  );
}

export default ImageTestimonialsGallery;
